import {
  ArrowRight,
  ArrowUpRight,
  Award,
  CheckCircle2,
  FileBadge,
  Flame,
  Handshake,
  HardHat,
  Headphones,
  Network,
  ScrollText,
  ShieldCheck,
  Sparkles,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { Container } from '../../components/layout/Container';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Reveal } from '../../components/motion/Reveal';
import { BreadcrumbListLd } from '../../components/seo/jsonld/BreadcrumbList';
import { cn } from '../../lib/cn';
import { ROUTES } from '@shared/constants/routes';

// ════════════════════════════════════════════════════════════════
// Données éditoriales — éditables sans toucher au composant
// ════════════════════════════════════════════════════════════════

type ToneKey = 'brand' | 'lavender' | 'sage' | 'accent';

interface PartnerProfile {
  id: 'artisan' | 'delegataire' | 'oblige' | 'apporteur';
  label: string;
  /** badge court à côté du titre */
  pill: string;
  /** phrase d'accroche (~140 c.) */
  tagline: string;
  /** description complète (~280 c.) */
  description: string;
  icon: LucideIcon;
  tone: ToneKey;
  /** mise en avant visuelle */
  highlight?: boolean;
  /** à qui ça s'adresse — 3 bullet points courts */
  audience: string[];
  /** ce que SEF apporte concrètement — 4 bullet points */
  benefits: string[];
  /** lien primaire (CTA) */
  primaryCta: { label: string; href: string };
  /** lien secondaire (CTA optionnel) */
  secondaryCta?: { label: string; href: string };
}

const PARTNERS: PartnerProfile[] = [
  {
    id: 'artisan',
    label: 'Artisan RGE',
    pill: 'Mandat CEE · service phare',
    tagline:
      "Vous réalisez les travaux. Nous montons, déposons et valorisons votre dossier CEE — sans avance de trésorerie de votre côté.",
    description:
      "Pour les entreprises qualifiées RGE (QualiPAC, Qualibat, Qualibois, Qualisol, Éco-Artisan…) qui veulent industrialiser leurs chantiers CEE et déléguer la partie administrative à un mandataire dédié, conforme au décret du 25 mars 2022.",
    icon: HardHat,
    tone: 'accent',
    highlight: true,
    audience: [
      'Entreprise du bâtiment qualifiée RGE en France',
      'TPE / PME de la rénovation énergétique',
      'Réseau d\'installateurs PAC, isolation, VMC, ECS thermo, solaire',
    ],
    benefits: [
      'Constitution & contrôle qualité du dossier en interne',
      "Dépôt sur le registre officiel des CEE + suivi jusqu'à la valorisation finale",
      "Aucune avance de trésorerie demandée à l'artisan",
      'Reporting partenaire hebdomadaire — primes et statuts par chantier',
    ],
    primaryCta: { label: 'Découvrir l\'espace artisan', href: ROUTES.artisans },
    secondaryCta: {
      label: 'Candidater au mandat CEE',
      href: `${ROUTES.partenaireCandidater}?profile=artisan`,
    },
  },
  {
    id: 'delegataire',
    label: 'Délégataire CEE',
    pill: 'Délégation officielle CEE',
    tagline:
      "Vous portez une délégation d'obligation CEE. Sourcez et valorisez du gisement CEE qualifié via notre réseau d'artisans RGE partenaires.",
    description:
      "Pour les délégataires CEE référencés au registre officiel qui souhaitent diversifier leurs canaux de sourcing tout en sécurisant la qualité documentaire des dossiers déposés. Tous les flux passent par une chaîne de contrôle interne conforme à la charte qualité officielle.",
    icon: Network,
    tone: 'lavender',
    audience: [
      "Délégataires titulaires d'une délégation officielle CEE active",
      'Bureaux de valorisation, groupements professionnels',
      'Acteurs publics ou parapublics en charge de programmes CEE',
    ],
    benefits: [
      'Sourcing qualifié via réseau d\'artisans RGE partenaires',
      'Conformité décret du 25 mars 2022 dès la constitution',
      'Conservation des pièces 9 ans, archivage tracé',
      'Cadre de partenariat clair, sans frais cachés',
    ],
    primaryCta: {
      label: 'Candidater comme délégataire',
      href: `${ROUTES.partenaireCandidater}?profile=delegataire`,
    },
    secondaryCta: { label: 'Lire nos CGV', href: ROUTES.legal.cgv },
  },
  {
    id: 'oblige',
    label: 'Obligé énergie',
    pill: 'Fournisseur soumis à obligation CEE',
    tagline:
      "Vous êtes fournisseur d'énergie soumis à obligation. Nous vous apportons un volume CEE qualifié, traçable et conforme — avec un partenariat assumé.",
    description:
      "Pour les fournisseurs d'électricité, gaz, fioul, GPL, chaleur ou carburants soumis à obligation par l'administration en charge des CEE. Nous proposons des accords de valorisation transparents, alignés avec votre stratégie de conformité.",
    icon: Flame,
    tone: 'brand',
    audience: [
      "Fournisseurs d'électricité, gaz, fioul, GPL, chaleur",
      "Distributeurs de carburants soumis à obligation CEE",
      "Acteurs énergie cherchant un partenariat structurant",
    ],
    benefits: [
      'Volumes CEE qualifiés, gisement diversifié (résidentiel, tertiaire, agri)',
      'Contrôle qualité documentaire de bout en bout',
      'Traçabilité complète — décret 25 mars 2022 + arrêté charte',
      'Reporting consolidé et traçable',
    ],
    primaryCta: {
      label: 'Candidater comme obligé',
      href: `${ROUTES.partenaireCandidater}?profile=oblige`,
    },
    secondaryCta: { label: 'À propos de SEF', href: ROUTES.aPropos },
  },
  {
    id: 'apporteur',
    label: "Apporteur d'affaires",
    pill: 'Prescripteur & recommandation',
    tagline:
      "Vous croisez chaque semaine des prospects qui ont besoin de rénover, comparer leur contrat énergie ou valoriser un chantier ? Adressez-les-nous.",
    description:
      "Programme ouvert aux consultants, courtiers en assurance, syndics, gestionnaires de patrimoine, AMO, bureaux d'études, agences immobilières et toute structure en contact régulier avec particuliers, professionnels, copropriétés ou collectivités.",
    icon: Handshake,
    tone: 'sage',
    audience: [
      'Consultants, courtiers, AMO, bureaux d\'études',
      'Syndics, gestionnaires de copropriétés',
      'Conseillers patrimoine, agences immo, professions libérales',
    ],
    benefits: [
      'Contrat de mandat d\'apport simple et explicite',
      'Suivi des dossiers transmis dans un espace dédié',
      'Versement au dossier abouti, sans frais cachés',
      'Co-branding possible (présentation client, supports)',
    ],
    primaryCta: {
      label: 'Candidater comme apporteur',
      href: `${ROUTES.partenaireCandidater}?profile=apporteur`,
    },
    secondaryCta: { label: 'Voir notre méthode', href: '#processus' },
  },
];

const TONE_STYLES: Record<
  ToneKey,
  { bg: string; ring: string; text: string; iconBg: string; iconText: string; gradient: string }
> = {
  brand: {
    bg: 'bg-brand-50/50',
    ring: 'ring-brand-200/60',
    text: 'text-brand-800',
    iconBg: 'bg-brand-100',
    iconText: 'text-brand-700',
    gradient: 'from-brand-50 via-paper to-brand-50/40',
  },
  lavender: {
    bg: 'bg-lavender-50/50',
    ring: 'ring-lavender-200/60',
    text: 'text-lavender-800',
    iconBg: 'bg-lavender-100',
    iconText: 'text-lavender-700',
    gradient: 'from-lavender-50 via-paper to-lavender-50/40',
  },
  sage: {
    bg: 'bg-sage-100/70',
    ring: 'ring-stone-200/60',
    text: 'text-sage-500',
    iconBg: 'bg-sage-200/80',
    iconText: 'text-sage-500',
    gradient: 'from-sage-100 via-paper to-brand-50/40',
  },
  accent: {
    bg: 'bg-accent-50/60',
    ring: 'ring-accent-200/70',
    text: 'text-accent-800',
    iconBg: 'bg-accent-100',
    iconText: 'text-accent-800',
    gradient: 'from-accent-50 via-paper to-brand-50/40',
  },
};

const TRUST = [
  {
    icon: ShieldCheck,
    title: 'Conformité documentaire',
    desc: 'Décret du 25 mars 2022, arrêté charte qualité, conservation des pièces 9 ans : nos process respectent strictement le cadre réglementaire CEE.',
  },
  {
    icon: ScrollText,
    title: 'Modèle transparent',
    desc: 'Pour chaque mode de partenariat, le fonctionnement est précisé dans le contrat avant toute signature. Aucun frais caché.',
  },
  {
    icon: Users,
    title: 'Une équipe dédiée',
    desc: 'Un référent unique côté SEF par partenaire actif. Suivi de dossier, points de pilotage trimestriels, reporting consolidé sur demande.',
  },
  {
    icon: Award,
    title: 'Trois pôles, un standard',
    desc: 'Travaux & rénovation, courtage énergie B2B, mandataire CEE : un même niveau d\'exigence sur la traçabilité, la qualité et la conformité.',
  },
];

const PROCESS_STEPS = [
  {
    n: '01',
    title: 'Premier contact',
    desc: 'Vous nous adressez votre profil via le formulaire — sous 24 h ouvrées, un référent commercial vous rappelle pour qualifier votre besoin.',
  },
  {
    n: '02',
    title: 'Étude de votre dossier',
    desc: "Vérification des pièces (RGE, agrément, KBis, délégation officielle CEE, registre Orias…). Cette étape conditionne la formalisation du partenariat.",
  },
  {
    n: '03',
    title: 'Cadrage juridique & économique',
    desc: 'Présentation du contrat-type, des engagements réciproques et de la grille de rémunération. Aucun engagement avant signature explicite.',
  },
  {
    n: '04',
    title: 'Activation & démarrage',
    desc: 'Accès à votre espace partenaire, formation de vos équipes sur nos outils, transmission des premiers dossiers ou premières opportunités.',
  },
];

const FAQ: Array<{ q: string; a: string }> = [
  {
    q: 'Comment êtes-vous rémunérés et qui paie quoi ?',
    a: "Sur le mandat CEE, l’artisan ne nous avance jamais de trésorerie : nous valorisons les certificats auprès de nos partenaires obligés et délégataires, et la valorisation revient à l’entreprise sous forme de prime versée à chaque chantier. Sur l’apport d’affaires, la rémunération est versée uniquement quand le dossier est abouti côté client. Notre fonctionnement est précisé dans le mandat signé avant le démarrage de la collaboration.",
  },
  {
    q: "Suis-je tenu de signer un mandat exclusif ?",
    a: "Non. Nos contrats de mandat CEE et d’apport d’affaires ne sont pas exclusifs. Vous restez libre de travailler en parallèle avec d’autres mandataires ou délégataires. Nous misons sur la qualité de service et la rapidité de paiement plutôt que sur la captivité.",
  },
  {
    q: "Quelles qualifications dois-je détenir pour devenir artisan partenaire ?",
    a: "Au minimum une qualification RGE active (QualiPAC, Qualibat 8611, Qualibois, Qualisol, Éco-Artisan ou équivalent) couvrant les opérations CEE que vous souhaitez valoriser. Nous vérifions la qualification en direct sur le registre officiel des qualifications RGE et complétons par un audit pièces (KBis, attestation décennale, assurance RC pro).",
  },
  {
    q: "Êtes-vous indépendants des obligés CEE ?",
    a: "Sur le pôle mandataire CEE, nous assumons des partenariats commerciaux structurants avec plusieurs obligés et délégataires : l’adjectif « indépendant » serait trompeur. Sur le pôle courtage énergie B2B en revanche, nous sommes effectivement indépendants des fournisseurs d’énergie (aucun lien capitalistique, aucune offre exclusive).",
  },
  {
    q: "Comment se passe le contrôle qualité des dossiers ?",
    a: "Chaque dossier passe par notre cellule de contrôle interne (vérification des pièces, vérification de l’éligibilité de la fiche CEE retenue, croisement des dates et des bénéficiaires). Nos process suivent strictement le décret du 25 mars 2022 sur les contrôles CEE et l’arrêté charte qualité.",
  },
  {
    q: "Sous quel délai un dossier est-il valorisé ?",
    a: "Comptez environ 14 jours entre le dépôt complet du dossier et sa validation par nos équipes, puis un paiement sous 30 à 60 jours selon le canal de valorisation choisi. Le calendrier exact est précisé dans le contrat de mandat avant signature.",
  },
];

// ════════════════════════════════════════════════════════════════
// Composant principal
// ════════════════════════════════════════════════════════════════

export default function DevenirPartenairePage() {
  return (
    <div className="bg-paper">
      {/* ─────────────── HERO ─────────────── */}
      <section className="relative overflow-hidden border-b border-stone-200/70 bg-stone-50/60 py-16 md:py-20">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 right-1/4 h-72 w-72 rounded-full bg-brand-500/10 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-24 left-1/3 h-64 w-64 rounded-full bg-accent-500/10 blur-3xl"
        />
        <Container className="relative max-w-4xl">
          <Reveal>
            <p className="eyebrow">
              <Sparkles className="h-3 w-3 text-brand-600" strokeWidth={2} />
              <span>Réseau de partenariats</span>
            </p>
            <h1 className="mt-4 font-display text-display-2 font-bold tracking-tight text-balance text-ink">
              Devenir partenaire de{' '}
              <span className="text-gradient-soft">Solution Éco France</span>.
            </h1>
            <p className="mt-5 max-w-2xl text-[16px] leading-relaxed text-stone-600">
              Artisan RGE, délégataire, obligé ou apporteur d'affaires : quatre modes
              de partenariat distincts, un seul standard de conformité documentaire.
              Pas de mandat exclusif, pas de frais cachés — et 0&nbsp;€ d'avance trésorerie
              pour les artisans RGE partenaires.
            </p>
          </Reveal>
          <Reveal delay={0.08} className="mt-7 flex flex-wrap gap-2">
            {[
              'Décret 25 mars 2022',
              'Arrêté charte qualité',
              'Conservation 9 ans',
              'RGPD strict',
            ].map((cert) => (
              <Badge key={cert} variant="default" size="md" className="gap-1.5">
                <CheckCircle2 className="h-3 w-3 text-brand-600" strokeWidth={2.4} />
                {cert}
              </Badge>
            ))}
          </Reveal>
          <Reveal delay={0.16} className="mt-8 flex flex-wrap items-center gap-3">
            <Button asChild size="lg" variant="brand">
              <a href="#profils" className="inline-flex items-center gap-1.5">
                Voir les 4 profils
                <ArrowRight className="h-4 w-4" strokeWidth={2.2} />
              </a>
            </Button>
            <Button asChild size="lg" variant="ghost">
              <a
                href={ROUTES.partenaireCandidater}
                className="inline-flex items-center gap-1.5"
              >
                Candidater directement
                <ArrowUpRight className="h-4 w-4" strokeWidth={2.2} />
              </a>
            </Button>
          </Reveal>
        </Container>
      </section>

      {/* ─────────────── 4 PROFILS PARTENAIRES ─────────────── */}
      <section id="profils" className="bg-paper py-16 scroll-mt-20">
        <Container className="max-w-5xl">
          <Reveal>
            <div className="max-w-2xl">
              <p className="eyebrow">4 modes de partenariat</p>
              <h2 className="mt-3 h-section">
                Choisissez le mode qui vous correspond.
              </h2>
              <p className="mt-3 text-[14.5px] leading-relaxed text-stone-600">
                Chaque profil donne lieu à un contrat-type spécifique, à une grille de
                rémunération transparente et à un référent commercial dédié.
              </p>
            </div>
          </Reveal>

          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {PARTNERS.map((p, i) => {
              const Icon = p.icon;
              const tone = TONE_STYLES[p.tone];
              return (
                <Reveal key={p.id} delay={i * 0.06}>
                  <article
                    id={p.id}
                    className={cn(
                      'group relative flex h-full flex-col gap-5 rounded-2xl border bg-paper p-6 shadow-soft transition-all duration-200 ease-out scroll-mt-20',
                      'hover:-translate-y-0.5 hover:shadow-elevated',
                      p.highlight ? 'border-accent-200/70' : 'border-stone-200',
                    )}
                  >
                    {p.highlight && (
                      <Badge variant="accent" size="sm" className="absolute right-4 top-4">
                        <Sparkles className="h-3 w-3" strokeWidth={2.4} />
                        Le plus demandé
                      </Badge>
                    )}

                    <div className="flex items-start gap-4">
                      <div
                        className={cn(
                          'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-1 ring-inset',
                          tone.iconBg,
                          tone.ring,
                        )}
                      >
                        <Icon className={cn('h-5 w-5', tone.iconText)} strokeWidth={1.7} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-400">
                          {p.pill}
                        </p>
                        <h3 className="mt-1 font-display text-h-pillar font-bold leading-tight text-ink">
                          {p.label}
                        </h3>
                      </div>
                    </div>

                    <p className="text-[14.5px] leading-relaxed text-stone-700">
                      {p.tagline}
                    </p>
                    <p className="text-[13.5px] leading-relaxed text-stone-500">
                      {p.description}
                    </p>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-400">
                          À qui ça s'adresse
                        </p>
                        <ul className="mt-2.5 space-y-1.5">
                          {p.audience.map((line) => (
                            <li
                              key={line}
                              className="flex items-start gap-1.5 text-[12.5px] leading-snug text-stone-600"
                            >
                              <span
                                aria-hidden
                                className="mt-1 h-1 w-1 shrink-0 rounded-full bg-stone-400"
                              />
                              <span>{line}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-400">
                          Ce que nous apportons
                        </p>
                        <ul className="mt-2.5 space-y-1.5">
                          {p.benefits.map((line) => (
                            <li
                              key={line}
                              className="flex items-start gap-1.5 text-[12.5px] leading-snug text-stone-700"
                            >
                              <CheckCircle2
                                className={cn('mt-0.5 h-3 w-3 shrink-0', tone.iconText)}
                                strokeWidth={2.4}
                              />
                              <span>{line}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-stone-200/70 pt-4">
                      <Button asChild size="md" variant={p.highlight ? 'brand' : 'primary'}>
                        <a href={p.primaryCta.href} className="inline-flex items-center gap-1.5">
                          {p.primaryCta.label}
                          <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.2} />
                        </a>
                      </Button>
                      {p.secondaryCta && (
                        <Button asChild size="md" variant="ghost">
                          <a
                            href={p.secondaryCta.href}
                            className="inline-flex items-center gap-1.5"
                          >
                            {p.secondaryCta.label}
                            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.2} />
                          </a>
                        </Button>
                      )}
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      {/* ─────────────── POURQUOI NOUS ─────────────── */}
      <section className="border-y border-stone-200/70 bg-stone-50/50 py-16">
        <Container className="max-w-5xl">
          <Reveal>
            <div className="max-w-2xl">
              <p className="eyebrow">Pourquoi nous</p>
              <h2 className="mt-3 h-section">
                Un standard de transparence aligné sur les 3 pôles.
              </h2>
              <p className="mt-3 text-[14.5px] leading-relaxed text-stone-600">
                Travaux & rénovation, courtage énergie B2B, mandataire CEE : nos partenaires
                bénéficient du même niveau d'exigence côté conformité, qualité et reporting.
              </p>
            </div>
          </Reveal>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {TRUST.map((t, i) => {
              const Icon = t.icon;
              return (
                <Reveal key={t.title} delay={i * 0.05}>
                  <div className="h-full rounded-2xl border border-stone-200 bg-paper p-5 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-elevated">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50">
                      <Icon className="h-4 w-4 text-brand-700" strokeWidth={1.7} />
                    </div>
                    <h3 className="mt-4 font-display text-[15px] font-semibold leading-tight text-ink">
                      {t.title}
                    </h3>
                    <p className="mt-1.5 text-[12.5px] leading-relaxed text-stone-600">
                      {t.desc}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      {/* ─────────────── PROCESSUS ─────────────── */}
      <section id="processus" className="bg-paper py-16 scroll-mt-20">
        <Container className="max-w-4xl">
          <Reveal>
            <div className="max-w-2xl">
              <p className="eyebrow">Notre méthode</p>
              <h2 className="mt-3 h-section">
                4 étapes, zéro engagement avant signature.
              </h2>
              <p className="mt-3 text-[14.5px] leading-relaxed text-stone-600">
                De la prise de contact au premier dossier transmis, comptez en moyenne
                2 à 4 semaines. Vous restez libre à chaque étape.
              </p>
            </div>
          </Reveal>

          <div className="mt-10 relative">
            <div
              aria-hidden
              className="absolute left-[26px] top-3 bottom-3 w-px bg-stone-200"
            />
            <ol className="space-y-5">
              {PROCESS_STEPS.map((step, i) => (
                <Reveal key={step.n} delay={i * 0.06}>
                  <li className="relative flex gap-5">
                    <div className="relative z-10 flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-xl bg-brand-50 font-display text-[15px] font-bold text-brand-700 ring-1 ring-inset ring-brand-200/60">
                      {step.n}
                    </div>
                    <div className="flex-1 rounded-xl border border-stone-200 bg-stone-50/40 p-5">
                      <h3 className="font-display text-[15px] font-semibold leading-tight text-ink">
                        {step.title}
                      </h3>
                      <p className="mt-1.5 text-[13.5px] leading-relaxed text-stone-600">
                        {step.desc}
                      </p>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </Container>
      </section>

      {/* ─────────────── FAQ ─────────────── */}
      <section className="bg-stone-50/50 py-16">
        <Container className="max-w-3xl">
          <Reveal>
            <div className="max-w-2xl">
              <p className="eyebrow">FAQ partenaires</p>
              <h2 className="mt-3 h-section">
                Les questions qu'on nous pose le plus souvent.
              </h2>
              <p className="mt-3 text-[14.5px] leading-relaxed text-stone-600">
                Sur la rémunération, l'exclusivité, les qualifications attendues et nos process
                de contrôle. La vôtre n'y est pas ? Notre équipe vous répond sous 24 h ouvrées.
              </p>
            </div>
          </Reveal>

          <div className="mt-8 space-y-3">
            {FAQ.map((item, i) => (
              <Reveal key={item.q} delay={i * 0.04}>
                <details className="group rounded-xl border border-stone-200 bg-paper transition-colors open:border-brand-200 open:bg-brand-50/40">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-5">
                    <span className="font-display text-[14.5px] font-semibold leading-tight text-ink">
                      {item.q}
                    </span>
                    <span
                      aria-hidden
                      className="relative inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-stone-200 bg-paper text-stone-500 transition-all duration-200 ease-out group-open:rotate-45 group-open:border-brand-300 group-open:bg-brand-50 group-open:text-brand-700"
                    >
                      <span className="block h-3 w-px bg-current" />
                      <span className="absolute block h-px w-3 bg-current" />
                    </span>
                  </summary>
                  <div className="px-5 pb-5 -mt-1 text-[13.5px] leading-relaxed text-stone-600">
                    {item.a}
                  </div>
                </details>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* ─────────────── CTA FINAL ─────────────── */}
      <section className="bg-paper pb-20 pt-16">
        <Container className="max-w-5xl">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-brand-200/60 bg-gradient-to-br from-brand-50 via-paper to-accent-50/40 px-6 py-10 shadow-soft sm:px-12 sm:py-12">
              <div
                aria-hidden
                className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-brand-500/10 blur-3xl"
              />
              <div
                aria-hidden
                className="pointer-events-none absolute -bottom-24 -left-10 h-48 w-48 rounded-full bg-accent-500/10 blur-3xl"
              />
              <div className="relative grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-center">
                <div>
                  <p className="eyebrow">Prêt à candidater ?</p>
                  <h2 className="mt-3 h-section">
                    Un référent partenaires vous rappelle sous{' '}
                    <span className="text-gradient-soft">24 h ouvrées</span>.
                  </h2>
                  <p className="mt-3 max-w-xl text-[14.5px] leading-relaxed text-stone-600">
                    Décrivez votre profil et vos volumes en quelques lignes. Vous recevez
                    en retour un cadrage économique et juridique adapté à votre cas — sans
                    aucun engagement.
                  </p>
                  <ul className="mt-5 grid gap-2 sm:grid-cols-2">
                    {[
                      'Pas de mandat exclusif',
                      'Pas d\'avance de trésorerie',
                      'Pas de frais cachés',
                      'CGV et mandat publiés en amont',
                    ].map((point) => (
                      <li
                        key={point}
                        className="flex items-start gap-1.5 text-[12.5px] leading-snug text-stone-700"
                      >
                        <CheckCircle2
                          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-600"
                          strokeWidth={2.4}
                        />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex flex-col gap-3 lg:items-end">
                  <Button asChild size="xl" variant="brand" className="w-full lg:w-auto">
                    <a
                      href={ROUTES.partenaireCandidater}
                      className="inline-flex items-center justify-center gap-1.5"
                    >
                      Candidater au programme
                      <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
                    </a>
                  </Button>
                  <Button asChild size="lg" variant="ghost" className="w-full lg:w-auto">
                    <a
                      href={ROUTES.legal.cgv}
                      className="inline-flex items-center justify-center gap-1.5"
                    >
                      <FileBadge className="h-4 w-4" strokeWidth={2} />
                      Lire nos CGV
                    </a>
                  </Button>
                  <Button asChild size="lg" variant="ghost" className="w-full lg:w-auto">
                    <a
                      href="mailto:partenariats@solutionecofrance.fr"
                      className="inline-flex items-center justify-center gap-1.5"
                    >
                      <Headphones className="h-4 w-4" strokeWidth={2} />
                      partenariats@solutionecofrance.fr
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: 'https://solutionecofrance.fr/' },
          {
            name: 'Devenir partenaire',
            url: 'https://solutionecofrance.fr/devenir-partenaire',
          },
        ]}
      />
    </div>
  );
}
